import { Box } from "@mui/joy";
import type { SxProps } from "@mui/joy/styles/types";
import type { ReactNode } from "react";

/**
 * Inner container for landing sections with consistent max-width
 * Use this inside LandingSection for content that needs narrower width
 *
 * Max-width options:
 * - "xs": 480px - Very narrow (forms, small cards)
 * - "sm": 640px - Narrow (single column content)
 * - "md": 768px - Medium (text-heavy sections)
 * - "lg": 960px - Large (most content)
 * - "xl": 1200px - Extra large (full-width content)
 * - "full": 100% - No max-width constraint
 */

type ContainerSize = "xs" | "sm" | "md" | "lg" | "xl" | "full";

interface LandingContainerProps {
  children: ReactNode;
  size?: ContainerSize;
  /** Center the container horizontally */
  centered?: boolean;
  /** Additional sx props */
  sx?: SxProps;
}

const sizeMap: Record<ContainerSize, number | string> = {
  xs: 480,
  sm: 640,
  md: 768,
  lg: 960,
  xl: 1200,
  full: "100%",
};

export const LandingContainer = ({
  children,
  size = "lg",
  centered = true,
  sx,
}: LandingContainerProps) => {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: sizeMap[size],
        mx: centered ? "auto" : 0,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};
